import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { formatInstantForViewer, formatUtcNaiveInstantForViewer, getViewerTimezone, resolveTimezoneFromState, toUtcInstant } from '../utils/dateTime';
import SharePickerModal from '../components/jobs/SharePickerModal';
import AssignJobModal from '../components/jobs/AssignJobModal';
import { subscribeToSrEvents } from '../services/webPushService';
import './JobsPage.css';
import './TransactionDetailPage.css';
import { getServiceTypeLabel } from '../utils/serviceTypeLabel';

// Local copy of JobsPage's tolerant list-shape unwrap — the companyVendors
// endpoint returns either a bare array or a wrapped object depending on the
// caller path, so we normalise before rendering the picker.
function extractArray(data) {
  if (Array.isArray(data)) return data;
  return data?.services || data?.content || data?.data || [];
}

// ── Constants (mirrors JobsPage.js) ─────────────────────────────────────────
const STATUS_PILL = {
  AWAITING_ASSIGNMENT: { label: 'Needs Assignment', cls: 'new' },
  PENDING:     { label: 'New Request', cls: 'new' },
  ACCEPTED:    { label: 'Confirmed',   cls: 'confirmed' },
  SCHEDULED:   { label: 'Confirmed',   cls: 'confirmed' },
  IN_PROGRESS: { label: 'In Progress', cls: 'inprogress' },
  COMPLETED:   { label: 'Done',        cls: 'done' },
  DECLINED:    { label: 'Declined',    cls: 'declined' },
  CANCELLED:   { label: 'Cancelled',   cls: 'cancelled' },
};

// Action buttons shown on the IN_PROGRESS step (from availableActions in API)
const ACTION_CFG = {
  ACCEPT:             { label: 'Accept job',         variant: 'primary', icon: 'check' },
  DECLINE:            { label: 'Decline',             variant: 'danger',  icon: 'x' },
  CONFIRM_SCHEDULE:   { label: 'Set schedule',        variant: 'primary', icon: 'check' },
  REQUEST_RESCHEDULE: { label: 'Reschedule',          variant: 'default', icon: null },
  MARK_ON_SITE:       { label: 'Mark on site',        variant: 'default', icon: 'key' },
  START_JOB:          { label: 'Start job',           variant: 'primary', icon: 'check' },
  UPLOAD_REPORT:      { label: 'Upload report',       variant: 'upload',  icon: 'upload' },
  MARK_COMPLETE:      { label: 'Mark complete',       variant: 'success', icon: 'checkCircle' },
  MARK_PAYMENT_RECEIVED: { label: 'Mark payment received', variant: 'success', icon: 'checkCircle' },
  PROVIDE_ACCESS_INFO:{ label: 'Provide access info', variant: 'default', icon: 'key' },
};

// The backend's 6-step timeline never actually populates `availableActions` on
// a step (confirmed via live response inspection — steps only carry id/
// stepNumber/name/status/completedAt), so the "current step" always fell
// through to a bare "In progress" label with no action buttons at all,
// including "Upload report". The 6 step names are fixed/well-known, so derive
// the same actions from stepNumber instead. Only used when availableActions
// is genuinely absent — if the backend starts sending it, that takes
// precedence and this fallback never applies.
const STEP_ACTION_FALLBACK = {
  2: ['ACCEPT', 'DECLINE'],           // "Accept or Decline"
  3: ['CONFIRM_SCHEDULE'],            // "Confirm Schedule"
  4: ['MARK_ON_SITE'],                // "Property Access"
  5: ['UPLOAD_REPORT'],               // "Upload Report"
  6: ['MARK_PAYMENT_RECEIVED'],       // "Payment Received"
  7: ['MARK_COMPLETE'],               // "Mark Job Complete"
};

// 12-hour Scheduled-time picker — replaces the native <input type="time">
// (which renders in 24-hour form in most browsers' default locales) with
// a popover-style picker mirroring the visual language of the calendar
// date picker above it: click-to-open, click-outside-to-close, Clear +
// Done actions at the bottom. Hour/minute/AM-PM columns are button
// grids, so a single tap per column completes the pick.
//
// Reads/writes the same "HH:mm" 24-hour string the rest of the schedule
// flow expects — the parent's toUtcInstant + proposedTime path stays
// untouched. Only emits up to onChange once all three legs are set.
function TimePicker12h({ value, onChange, disabled }) {
  const parseValue = (v) => {
    if (typeof v !== 'string' || !/^(\d{2}):(\d{2})$/.test(v)) {
      return { hour12: null, minute: null, meridiem: 'AM' };
    }
    const [h, m] = v.split(':').map(Number);
    return {
      hour12: h % 12 === 0 ? 12 : h % 12,
      minute: m,
      meridiem: h >= 12 ? 'PM' : 'AM',
    };
  };
  const [state, setState] = useState(() => parseValue(value));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Re-sync when the parent resets scheduleTime (e.g. after Save clears it).
  useEffect(() => { setState(parseValue(value)); }, [value]);

  // Click-outside closes the popover — same interaction as the native
  // date picker's calendar sheet.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const commit = (next) => {
    setState(next);
    if (next.hour12 != null && next.minute != null && next.meridiem) {
      let h24 = next.hour12 % 12;
      if (next.meridiem === 'PM') h24 += 12;
      onChange(
        `${String(h24).padStart(2, '0')}:${String(next.minute).padStart(2, '0')}`,
      );
    }
  };

  const displayLabel = (() => {
    if (state.hour12 == null || state.minute == null) return 'hh:mm --';
    return `${state.hour12}:${String(state.minute).padStart(2, '0')} ${state.meridiem}`;
  })();

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5-min steps

  return (
    <div className="jp-time12-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`jp-time12-trigger ${state.hour12 == null ? 'is-empty' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span>{displayLabel}</span>
        <ClockGlyph />
      </button>
      {open && (
        <div className="jp-time12-popover">
          <div className="jp-time12-preview">{displayLabel}</div>
          <div className="jp-time12-cols">
            <div className="jp-time12-col">
              <div className="jp-time12-col-title">Hour</div>
              <div className="jp-time12-grid">
                {hours.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className={`jp-time12-cell ${state.hour12 === h ? 'is-active' : ''}`}
                    onClick={() => commit({ ...state, hour12: h })}
                  >{h}</button>
                ))}
              </div>
            </div>
            <div className="jp-time12-col">
              <div className="jp-time12-col-title">Minute</div>
              <div className="jp-time12-grid">
                {minutes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`jp-time12-cell ${state.minute === m ? 'is-active' : ''}`}
                    onClick={() => commit({ ...state, minute: m })}
                  >{String(m).padStart(2, '0')}</button>
                ))}
              </div>
            </div>
            <div className="jp-time12-col jp-time12-col-meridiem">
              <div className="jp-time12-col-title">AM/PM</div>
              <div className="jp-time12-meridiem-stack">
                <button
                  type="button"
                  className={`jp-time12-cell ${state.meridiem === 'AM' ? 'is-active' : ''}`}
                  onClick={() => commit({ ...state, meridiem: 'AM' })}
                >AM</button>
                <button
                  type="button"
                  className={`jp-time12-cell ${state.meridiem === 'PM' ? 'is-active' : ''}`}
                  onClick={() => commit({ ...state, meridiem: 'PM' })}
                >PM</button>
              </div>
            </div>
          </div>
          <div className="jp-time12-actions">
            <button
              type="button"
              className="jp-time12-action-clear"
              onClick={() => {
                setState({ hour12: null, minute: null, meridiem: 'AM' });
                onChange('');
              }}
            >Clear</button>
            <button
              type="button"
              className="jp-time12-action-done"
              onClick={() => setOpen(false)}
            >Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ClockGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (user) user.getIdToken(true).then(resolve).catch(reject);
      else reject(new Error('Not authenticated'));
    });
    setTimeout(() => { unsub(); reject(new Error('Auth timeout')); }, 10000);
  });

// Handles both a real 'Z'/offset-marked instant and a bare backend
// LocalDateTime (UTC digits, no marker) — renders in the viewer's own
// profile-state timezone either way, matching "Due By" above.
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return formatUtcNaiveInstantForViewer(iso).dateLabel || String(iso);
  } catch { return String(iso); }
}

// Backend sometimes leaves the requester's display name unresolved and sends
// the raw Firestore document ID instead (e.g. "NOCjI25skgXkydSf7mSE") — a bare
// alphanumeric key with no spaces, unlike any real person's name. Filter those
// out so the UI falls back to "Agent" rather than leaking an internal key.
function isRawIdLike(str) {
  return typeof str === 'string' && /^[A-Za-z0-9]{15,36}$/.test(str) && /\d/.test(str);
}
function cleanName(v) {
  return (v && !isRawIdLike(v)) ? v : undefined;
}

function normalizeJob(raw) {
  const status = raw.currentStatus || raw.status || '';
  return {
    id:          raw.serviceId || raw.id || raw.requestId || raw.serviceRequestId,
    serviceType: raw.serviceType || 'Service',
    // Display-only (custom name for "Other"); serviceType stays canonical for logic.
    serviceTypeLabel: getServiceTypeLabel(raw) || 'Service',
    status,
    address:     raw.propertyAddress
                   || raw.address
                   || (raw.property && raw.property.address)
                   || 'Unknown address',
    // requestedDate is the mobile app's authoritative "due by" field for this
    // same /service-requests endpoint — prefer it over the older guessed
    // field names, which stay as fallbacks for any older/different payload.
    date:        raw.requestedDate || raw.scheduledDate || raw.appointmentDate || raw.createdAt || null,
    clientName:  cleanName(raw.requesterName)
                   || cleanName(raw.requestedBy)
                   || cleanName(raw.agentName)
                   || [raw.agentFirstName, raw.agentLastName].filter(Boolean).join(' ')
                   || 'Agent',
    description: raw.description || '',
    // Firebase UID of the requester — this is what chat participants MUST
    // be keyed on (Firestore chat listeners subscribe by firebase uid, so a
    // User DB id here silently prevents the agent from seeing the chat).
    // requestedBy is the DB id and is NOT interchangeable — only use as a
    // last-resort fallback for legacy payloads.
    requesterUid: raw.requesterFirebaseUid || raw.requesterUid || null,
    requesterName: raw.requesterName || '',
    // Surfaced to the Share picker so the "Company" / "Inspector" row can
    // name the actual counterparty (e.g. "Greenline Home Inspections") in
    // its subtitle rather than generic role labels. providerId is also used
    // by the Company-view "Waiting for {inspector}" + Re-Assign block on the
    // Accept/Decline timeline step — we need the id to distinguish
    // "still routed to the Company itself" (== companyUserId, no Re-Assign
    // yet) from "routed to a staff inspector" (!= companyUserId, offer
    // Re-Assign).
    providerId:   raw.providerId || null,
    providerName: raw.providerName || raw.provider?.name || null,
    // Firebase UID of the assigned provider — required for opening a chat
    // with the inspector from the Chat tab (Firestore chat participants are
    // keyed on Firebase UID, not User DB id). Only Company viewers use this
    // to render the Inspector card alongside the Requester card; Individual
    // SPs never surface a second chat target here.
    providerFirebaseUid: raw.providerFirebaseUid || raw.provider?.firebaseUid || null,
    // Billing tab source-of-truth. Estimated + Payment Received are read-only
    // for the SP; Billed is the SP's own editable field (PATCH
    // /users/me/service-requests/{id}/billing).
    estimatedAmount:    raw.estimatedAmount ?? null,
    billedAmount:       raw.billedAmount ?? null,
    finalPaymentAmount: raw.finalPaymentAmount ?? null,
    // Needed to render the "you declined this, company is reassigning"
    // view for an inspector whose company-routed SR bounced back. Backend
    // stashes the declining inspector's id on previousProviderId; the SR's
    // status flips to AWAITING_ASSIGNMENT (not DECLINED) because the SR
    // itself is still alive on the company's queue.
    previousProviderId: raw.previousProviderId || null,
    routedByCompanyId:  raw.routedByCompanyId || null,
  };
}

// Originally mirrored the mobile app's formatRequestedDue() exactly (device-
// local, no viewer-zone conversion). Now renders in the *viewing* SP's own
// timezone instead, derived from their SP-profile State (see viewerTimezone
// in the component below) — requestedDate/requestedTime are already real
// UTC ISO instants end to end, so this is a pure display-side change with no
// backend involvement. Mobile still shows device-local time for this same
// field until it gets the equivalent update, so the two can diverge for a
// user who checks the same job on both — see dateTime.js's formatInstantForViewer.
function formatRequestedDue(isoDate, viewerTimezone) {
  return formatInstantForViewer(isoDate, viewerTimezone).dateLabel;
}

// "Due By Time" — see formatRequestedDue above for the viewer-timezone note.
function formatRequestedTime(isoDateTime, viewerTimezone) {
  return formatInstantForViewer(isoDateTime, viewerTimezone).timeLabel;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Defensive extraction of a report attachment from the SR object's top-level
// reportAttachments array (populates the Documents tab) — field names mirror
// the mobile app's ReportAttachment type, with fallbacks for older shapes.
function normalizeAttachment(a) {
  return {
    id:             a.id || a.attachmentId || a.reportId || null,
    fileName:       a.fileName || a.filename || a.name || 'Report',
    fileSize:       a.fileSize || a.size || 0,
    fileUrl:        a.fileUrl || a.url || a.downloadUrl || a.viewUrl || null,
    uploadedByRole: a.uploadedByRole || null,
    uploadedAt:     a.uploadedAt || a.createdAt || null,
    canDelete:      a.canDelete !== undefined ? !!a.canDelete : true,
    // Phase-1A visibility fields. isMine falls back to null so AttachmentRow's
    // canDelete fallback kicks in when the backend hasn't been rolled forward
    // yet (staging/prod trailing dev).
    isMine:         a.isMine !== undefined ? !!a.isMine : null,
    visibility:     a.visibility || null,
    sharedWith:     Array.isArray(a.sharedWith) ? a.sharedWith : [],
  };
}

function mapStepState(stepStatus) {
  switch (stepStatus) {
    case 'COMPLETED':   return 'done';
    case 'IN_PROGRESS': return 'active';
    case 'SKIPPED':     return 'skipped';
    default:            return 'todo';
  }
}

// Same identifier handleTimelineAction already uses for the TIMELINE-scope PATCH —
// reused as the report state's lookup key so both stay in sync.
function getStepKey(step) {
  return step.timelineEntryId || step.entryId || `step_${step.stepNumber}`;
}

// Defensive extraction of a report attachment that may already be embedded on a
// timeline step from the API (field name isn't finalized upstream yet) — tries
// the common shapes so a previously uploaded report still displays after a
// page refresh instead of only showing up right after this session's own upload.
function extractStepReport(step) {
  const r = step?.report || step?.reportFile || step?.attachment || step?.uploadedReport || step?.metadata?.report;
  if (!r) return null;
  const fileUrl = r.fileUrl || r.url || r.downloadUrl || r.viewUrl || null;
  const attachmentId = r.attachmentId || r.id || r.reportId || null;
  if (!fileUrl && !attachmentId) return null;
  return {
    attachmentId,
    fileName: r.fileName || r.filename || r.name || 'Report',
    fileUrl,
  };
}

// ── SVG icons ─────────────────────────────────────────────────────────────────
const CheckIcon = ({ size = 13, cls = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={cls}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const KeyIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);
const UploadIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const FileTextIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);
const MessageIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const CheckCircleIcon = ({ size = 13, cls = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ClockIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const LoaderIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'jp-spin 1s linear infinite' }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);
const CircleIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
  </svg>
);
const PencilIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);
const SkipIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);
const CloudUploadIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

// ── Step circle components ────────────────────────────────────────────────────
const StepDone = () => (
  <div className="jp-step-circle jp-step-done"><CheckIcon size={13} /></div>
);
const StepActive = () => (
  <div className="jp-step-circle jp-step-active">
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
  </div>
);
const StepTodo = () => (
  <div className="jp-step-circle jp-step-todo"><CircleIcon size={10} /></div>
);
const StepSkipped = () => (
  <div className="jp-step-circle jp-step-skipped"><SkipIcon size={10} /></div>
);

// ── Action button ─────────────────────────────────────────────────────────────
const ActionBtn = ({ children, variant = 'default', onClick, disabled }) => (
  <button type="button" className={`jp-action-btn jp-action-btn-${variant}`} onClick={onClick} disabled={disabled}>
    {children}
  </button>
);

// Render icon by name from ACTION_CFG
function renderIcon(icon, size = 13) {
  switch (icon) {
    case 'check':       return <CheckIcon size={size} />;
    case 'x':           return <XIcon size={size} />;
    case 'key':         return <KeyIcon size={size} />;
    case 'upload':      return <UploadIcon size={size} />;
    case 'checkCircle': return <CheckCircleIcon size={size} />;
    default:            return null;
  }
}

// ── Step row ──────────────────────────────────────────────────────────────────
const StepRow = ({ state, name, date, showLine = true, children }) => (
  <div className="jp-step-row">
    <div className="jp-step-col">
      {state === 'done'    && <StepDone />}
      {state === 'active'  && <StepActive />}
      {state === 'todo'    && <StepTodo />}
      {state === 'skipped' && <StepSkipped />}
      {showLine && <div className="jp-step-line" />}
    </div>
    <div className="jp-step-body">
      <div className="jp-step-header">
        <p className={`jp-step-name ${state === 'todo' || state === 'skipped' ? 'jp-step-name-todo' : ''}`}>
          {name}
        </p>
        {date && (
          <p className={`jp-step-date ${state === 'todo' || state === 'skipped' ? 'jp-step-date-todo' : ''}`}>
            {date}
          </p>
        )}
      </div>
      {children}
    </div>
  </div>
);

// ── Description box (character-capped, with Show more/less) ────────────────────
// Used for both the sidebar description and the Accept/Decline step description
// (see the wireframe in the job description redesign task) — reuses the existing
// .jp-sidebar-description* box styling (colors/sizes/fonts unchanged) for both
// placements, just adding a 150-character cap + toggle so a long description can
// no longer push page/step content down.
const DESCRIPTION_CHAR_LIMIT = 150;

const DescriptionBox = ({ text, label = 'Description' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const trimmed = text.trim();
  const isLong = trimmed.length > DESCRIPTION_CHAR_LIMIT;
  // Cut at the last word boundary at/before the limit so we don't chop a word in half.
  const cutIndex = trimmed.lastIndexOf(' ', DESCRIPTION_CHAR_LIMIT);
  const truncateAt = cutIndex > 0 ? cutIndex : DESCRIPTION_CHAR_LIMIT;
  const shownText = !isLong || expanded ? trimmed : `${trimmed.slice(0, truncateAt).trimEnd()}…`;

  return (
    <div className="jp-sidebar-description">
      <div className="jp-sidebar-description-label">{label}</div>
      <div className="jp-sidebar-description-text">{shownText}</div>
      {isLong && (
        <span
          className="jp-description-toggle"
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((e) => !e)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpanded((v) => !v)}
        >
          {expanded ? 'Show less ▴' : 'Show more ▾'}
        </span>
      )}
    </div>
  );
};

// ── Dynamic timeline ──────────────────────────────────────────────────────────
const DynamicTimeline = ({
  timeline, loading, error, onAction, actionLoading,
  jobReports, reportUploading, onUploadReportClick, onDeleteReportClick,
  requestedDueBy, requestedDueTime, scheduledDate, scheduledTime, requesterName, description,
  // Full provider-uploaded attachment list on the SR. Used only for
  // step_5 so the Add-more + collapsible "Uploaded reports (N)" list
  // matches the mobile Timeline (mirror of spec §4 collapsible pattern).
  reportAttachments = [],
  onDeleteAttachmentClick,
  // Viewer + job context — used only by the step 2 Company-view branch.
  // Company viewers never see Accept/Decline on step 2; they see either
  // "Assign a service provider to continue" + Assign (for
  // AWAITING_ASSIGNMENT) or "Waiting for {inspector}" + Reassign (for
  // PENDING routed to a staff inspector). All optional so non-SP or
  // Individual viewers get the unchanged UI.
  viewerVendorType = null,
  companyUserId = null,
  jobStatus = null,
  jobProviderId = null,
  jobProviderName = null,
  isBouncedInspectorView = false,
  onReassignClick,
  onAssignClick,
}) => {
  // Per-instance expand state for the step_5 uploaded-reports dropdown.
  // Collapsed by default so the timeline stays compact.
  const [reportsExpanded, setReportsExpanded] = useState(false);
  // Step 3 "Set schedule" mini-form state. Blank defaults per product spec;
  // the requester's non-binding suggestion shows below the inputs as helper
  // text (not pre-filled) so the provider always makes an explicit choice.
  // `scheduleEditing` is used AFTER the step is done (before Property Access
  // starts) to re-open the form via a pencil affordance.
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const step4 = (timeline?.steps || []).find((s) => s.stepNumber === 4);
  // "Work has started" = Property Access COMPLETED (SP tapped Mark on site).
  // While step 4 is IN_PROGRESS the SP is still en route / hasn't checked in,
  // so Reschedule stays available per the reassignment-window rule.
  const isStep4Started = !!step4 && step4.status === 'COMPLETED';
  const providerAttachments = React.useMemo(
    () => (reportAttachments || []).filter(a => !a.uploadedByRole || a.uploadedByRole === 'PROVIDER'),
    [reportAttachments]
  );
  if (loading) return (
    <div className="jp-timeline-state">
      <LoaderIcon size={22} />
      <p>Loading timeline…</p>
    </div>
  );
  if (error) return (
    <div className="jp-timeline-state jp-timeline-error-state">
      <p>{error}</p>
    </div>
  );
  if (!timeline || !Array.isArray(timeline.steps) || timeline.steps.length === 0) return (
    <div className="jp-timeline-state">
      <p>No timeline data available.</p>
    </div>
  );

  return (
    <div className="jp-timeline">
      {timeline.steps.map((step, i) => {
        const isLast = i === timeline.steps.length - 1;
        const state = mapStepState(step.status);
        const date = step.completedAt ? fmtDate(step.completedAt) : '';
        const actions = step.availableActions || STEP_ACTION_FALLBACK[step.stepNumber] || [];
        const stepKey = getStepKey(step);
        const hasUploadReport = actions.includes('UPLOAD_REPORT');
        const stepReport = (jobReports && jobReports[stepKey]) || extractStepReport(step);
        const isUploadingThis = reportUploading === stepKey;

        return (
          <StepRow key={step.stepNumber} state={state} name={step.name} date={date} showLine={!isLast}>
            {step.stepNumber === 1 && (
              <>
                {/* "Due By Time" input was removed from Create SR; requester's
                    deadline is end-of-day. Drop the redundant "11:59 PM"
                    line and match the mobile SP screen which shows a single
                    date-only Due By subtitle. */}
                <p className="jp-step-subtitle">Due By Date: {requestedDueBy || '—'}</p>
                <p className="jp-step-subtitle">Requested By: {requesterName || 'Agent'}</p>
              </>
            )}
            {state === 'skipped' && <p className="jp-step-muted">Skipped</p>}
            {state === 'todo'    && <p className="jp-step-muted">Not started</p>}
            {state === 'active' && actions.length === 0 && (
              <p className="jp-step-muted">In progress</p>
            )}
            {/* Step 2 (Accept/Decline) — Company admin's view. The Accept
                and Decline actions ONLY belong to the assigned inspector;
                the backend 403s any COMPANY caller. Always suppress the
                actionable pair for a Company viewer and instead offer:
                  · AWAITING_ASSIGNMENT (or PENDING still on the company) →
                    "Assign a service provider to continue" + Assign
                  · PENDING routed to a staff inspector (providerId !=
                    companyUserId) → "Waiting for {inspector}" + Reassign */}
            {state === 'active'
              && step.stepNumber === 2
              && viewerVendorType === 'COMPANY'
              && jobStatus !== 'CANCELLED' && (
              (() => {
                const routedToStaff = jobStatus === 'PENDING'
                  && jobProviderId
                  && companyUserId
                  && jobProviderId !== companyUserId;
                if (routedToStaff) {
                  return (
                    <>
                      <p className="jp-step-muted">
                        Waiting for {jobProviderName || 'the assigned inspector'} to respond
                      </p>
                      <div className="jp-btn-row">
                        <ActionBtn
                          variant="default"
                          onClick={() => onReassignClick && onReassignClick()}
                          disabled={actionLoading !== null}
                        >
                          Reassign
                        </ActionBtn>
                      </div>
                      <DescriptionBox text={description} label="Service Description" />
                    </>
                  );
                }
                // AWAITING_ASSIGNMENT, or PENDING that never left the
                // company (rare edge — treat same as awaiting-assignment).
                return (
                  <>
                    <p className="jp-step-muted">
                      Assign a service provider to continue
                    </p>
                    <div className="jp-btn-row">
                      <ActionBtn
                        variant="primary"
                        onClick={() => onAssignClick && onAssignClick()}
                        disabled={actionLoading !== null}
                      >
                        Assign
                      </ActionBtn>
                    </div>
                    <DescriptionBox text={description} label="Service Description" />
                  </>
                );
              })()
            )}
            {/* Bounced-inspector view of step 2: SR bounced back to the
                company after THIS inspector declined it. Backend rewinds
                step 2 to IN_PROGRESS so a fresh assignee sees a live
                accept-or-decline card, but for the just-declined inspector
                the correct affordance is a "you declined this" line — the
                mutator would 403 them anyway (providerId is now the
                company). */}
            {state === 'active'
              && step.stepNumber === 2
              && isBouncedInspectorView && (
              <p className="jp-step-muted">
                You declined this job — waiting for the company to reassign.
              </p>
            )}
            {/* Step 3 "Set schedule" — mini-form with date + time inputs,
                requester's non-binding suggestion as helper text, and a
                pencil affordance to re-edit after saving (until step 4
                Property Access starts, per the reassignment-window rule). */}
            {/* Step 3 — schedule already saved (backend has proposedDate)
                and not currently editing. The plain-text "Scheduled: date ·
                time" persists for the life of the SR (even after work
                starts) so the SP can always see what was scheduled. The
                Reschedule button only shows while Property Access hasn't
                completed (reassignment-window rule). */}
            {step.stepNumber === 3
              && jobStatus !== 'CANCELLED'
              && !scheduleEditing
              && (scheduledDate || scheduledTime)
              && (state === 'active' || state === 'done') && (
              <>
                <p className="jp-step-subtitle">
                  Scheduled: {[scheduledDate, scheduledTime].filter(Boolean).join(' · ')}
                </p>
                {!isStep4Started && (
                  <div className="jp-btn-row">
                    <ActionBtn
                      variant="default"
                      onClick={() => setScheduleEditing(true)}
                      disabled={actionLoading !== null}
                    >
                      <PencilIcon size={13} />
                      Reschedule
                    </ActionBtn>
                  </div>
                )}
              </>
            )}
            {/* Step 3 form — either the initial "Set schedule" state (active
                + CONFIRM_SCHEDULE action + no schedule saved yet) or the
                Reschedule state (user tapped Reschedule; scheduleEditing
                true). */}
            {step.stepNumber === 3
              && jobStatus !== 'CANCELLED'
              && (scheduleEditing
                || (state === 'active'
                  && actions.includes('CONFIRM_SCHEDULE')
                  && !(scheduledDate || scheduledTime))) && (
              <>
                {state === 'active' && !(scheduledDate || scheduledTime) && (
                  <span className="jp-action-badge">
                    <ClockIcon size={11} />Action required
                  </span>
                )}
                <div className="jp-schedule-form">
                  <label className="jp-schedule-label">
                    Scheduled date
                    <input
                      type="date"
                      className="jp-schedule-input"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      disabled={actionLoading !== null}
                    />
                  </label>
                  <label className="jp-schedule-label">
                    Scheduled time
                    <TimePicker12h
                      value={scheduleTime}
                      onChange={setScheduleTime}
                      disabled={actionLoading !== null}
                    />
                  </label>
                  <div className="jp-btn-row">
                    <ActionBtn
                      variant="primary"
                      onClick={() => {
                        if (!scheduleDate || !scheduleTime) return;
                        // Mirror the Create Service Request save flow so the
                        // wire format stays consistent across every SR write
                        // site: proposedDate = date-only midnight in viewer
                        // TZ → UTC; proposedTime = date + time combined →
                        // UTC. Both come back through mapToResponse as
                        // OffsetDateTime and the requester's Details modal
                        // renders them in the viewer's own timezone.
                        const proposedDateIso = toUtcInstant(scheduleDate, null);
                        const proposedTimeIso = toUtcInstant(scheduleDate, scheduleTime);
                        onAction && onAction(step, 'CONFIRM_SCHEDULE', {
                          proposedDate: proposedDateIso,
                          proposedTime: proposedTimeIso,
                        });
                        setScheduleEditing(false);
                        setScheduleDate('');
                        setScheduleTime('');
                      }}
                      disabled={actionLoading !== null || !scheduleDate || !scheduleTime}
                    >
                      {actionLoading === `${step.stepNumber}_CONFIRM_SCHEDULE`
                        ? <LoaderIcon size={13} />
                        : <CheckIcon size={13} />}
                      {(scheduledDate || scheduledTime) ? 'Save schedule' : 'Set schedule'}
                    </ActionBtn>
                    {scheduleEditing && (
                      <ActionBtn
                        variant="default"
                        onClick={() => {
                          setScheduleEditing(false);
                          setScheduleDate('');
                          setScheduleTime('');
                        }}
                        disabled={actionLoading !== null}
                      >
                        Cancel
                      </ActionBtn>
                    )}
                  </div>
                </div>
              </>
            )}
            {state === 'active' && actions.length > 0
              && jobStatus !== 'CANCELLED'
              && !(step.stepNumber === 2 && viewerVendorType === 'COMPANY')
              && !(step.stepNumber === 2 && isBouncedInspectorView)
              && !(step.stepNumber === 3 && actions.includes('CONFIRM_SCHEDULE')) && (
              <>
                <span className="jp-action-badge">
                  <ClockIcon size={11} />Action required
                </span>
                <div className="jp-btn-row">
                  {actions.filter(a => a !== 'UPLOAD_REPORT').map(action => {
                    const cfg = ACTION_CFG[action];
                    if (!cfg) return null;
                    const actionKey = `${step.stepNumber}_${action}`;
                    const isThisLoading = actionLoading === actionKey;

                    // Property Access step — same MARK_ON_SITE call either way,
                    // just asked as a Yes/No question instead of one confirm button.
                    if (action === 'MARK_ON_SITE') {
                      return (
                        <React.Fragment key={action}>
                          <ActionBtn
                            variant="primary"
                            onClick={() => onAction && onAction(step, action)}
                            disabled={actionLoading !== null}
                          >
                            <CheckIcon size={13} />
                            Yes
                          </ActionBtn>
                          <ActionBtn
                            variant="default"
                            onClick={() => onAction && onAction(step, action)}
                            disabled={actionLoading !== null}
                          >
                            <XIcon size={13} />
                            No
                          </ActionBtn>
                        </React.Fragment>
                      );
                    }

                    return (
                      <ActionBtn
                        key={action}
                        variant={cfg.variant}
                        onClick={() => onAction && onAction(step, action)}
                        disabled={actionLoading !== null}
                      >
                        {isThisLoading ? <LoaderIcon size={13} /> : cfg.icon && renderIcon(cfg.icon, 13)}
                        {cfg.label}
                      </ActionBtn>
                    );
                  })}
                  {hasUploadReport && (
                    <>
                      <ActionBtn
                        variant="upload"
                        onClick={() => onUploadReportClick && onUploadReportClick(step)}
                        disabled={actionLoading !== null || isUploadingThis}
                      >
                        {isUploadingThis ? <LoaderIcon size={13} /> : <UploadIcon size={13} />}
                        Upload report
                      </ActionBtn>
                      <ActionBtn
                        variant="default"
                        onClick={() => onAction && onAction(step, 'UPLOAD_REPORT')}
                        disabled={actionLoading !== null || isUploadingThis}
                      >
                        N/A
                      </ActionBtn>
                    </>
                  )}
                </div>
                {step.stepNumber === 2 && (
                  <DescriptionBox text={description} label="Service Description" />
                )}
              </>
            )}
            {/* step_5 gets its own Add-more + "Uploaded reports (N)"
                collapsible whenever at least one provider file exists on
                the SR, matching the mobile Timeline UX. Other steps still
                fall back to the legacy single-stepReport row so any
                jobReports entries the wider timeline flow depends on
                stay rendered. */}
            {step.stepNumber === 5 && providerAttachments.length > 0 && (
              <div className="jp-report-attachments jp-timeline-reports">
                <button
                  type="button"
                  className="jp-timeline-addmore-btn"
                  onClick={() => onUploadReportClick && onUploadReportClick(step)}
                  disabled={actionLoading !== null || isUploadingThis}
                  title="Upload another report"
                >
                  {isUploadingThis ? <LoaderIcon size={12} /> : <UploadIcon size={12} />}
                  Add more
                </button>
                <button
                  type="button"
                  className="jp-timeline-reports-toggle"
                  onClick={() => setReportsExpanded(v => !v)}
                  aria-expanded={reportsExpanded}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  <span className="jp-timeline-reports-toggle-text">Uploaded reports</span>
                  <span className="jp-timeline-reports-count">{providerAttachments.length}</span>
                  <span className="jp-timeline-reports-spacer" />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {reportsExpanded
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="6 9 12 15 18 9" />}
                  </svg>
                </button>
                {reportsExpanded && providerAttachments.map(att => {
                  const fileUrl = att.fileUrl || att.url || att.downloadUrl;
                  return (
                    <div key={att.id || att.fileUrl} className="jp-report-file-row">
                      <FileTextIcon />
                      <span className="jp-report-file-name" title={att.fileName}>{att.fileName}</span>
                      <button
                        type="button"
                        className="jp-report-file-icon-btn"
                        title={fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
                        aria-label={fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
                        disabled={!fileUrl}
                        onClick={() => fileUrl && window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                      >
                        <EyeIcon />
                      </button>
                      {att.canDelete && (
                        <button
                          type="button"
                          className="jp-report-file-icon-btn jp-report-file-delete"
                          title="Delete"
                          onClick={() => onDeleteAttachmentClick && onDeleteAttachmentClick(att)}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {stepReport && step.stepNumber !== 5 && (
              <div className="jp-report-file-row">
                <FileTextIcon />
                <span className="jp-report-file-name" title={stepReport.fileName}>{stepReport.fileName}</span>
                <button
                  type="button"
                  className="jp-report-file-icon-btn"
                  title={stepReport.fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
                  aria-label={stepReport.fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
                  disabled={!stepReport.fileUrl}
                  onClick={() => stepReport.fileUrl && window.open(stepReport.fileUrl, '_blank', 'noopener,noreferrer')}
                >
                  <EyeIcon />
                </button>
                <button
                  type="button"
                  className="jp-report-file-icon-btn jp-report-file-delete"
                  title="Delete"
                  onClick={() => onDeleteReportClick && onDeleteReportClick(step, stepReport)}
                >
                  <TrashIcon />
                </button>
              </div>
            )}
          </StepRow>
        );
      })}
    </div>
  );
};

// ── Documents panel ───────────────────────────────────────────────────────────
const EmptyDocs = ({ onUploadClick }) => (
  <div className="jp-docs-panel">
    <div className="jp-docs-dropzone" onClick={onUploadClick} role={onUploadClick ? 'button' : undefined}>
      <CloudUploadIcon size={28} />
      <p>No documents yet</p>
      <span>PDF, image, or document · Max 25 MB</span>
    </div>
  </div>
);

// Spec §3 visibility matrix — empty sharedWith is PRIVATE; otherwise SHARED
// enumerates the granted audiences, rendering the viewer's own audience as
// "You" so the pill mirrors what the SP actually experiences.
const computePillLabel = (att, myAudience, srHasCompany) => {
  const raw = Array.isArray(att.sharedWith) ? att.sharedWith : [];
  // Direct-individual SRs have no Company audience — strip any legacy
  // COMPANY entry so the pill doesn't surface a bogus "Company" label
  // to an individual inspector.
  const shared = srHasCompany ? raw : raw.filter(a => a !== 'COMPANY');
  if (shared.length === 0) return { text: 'PRIVATE', shared: false };
  const labels = shared.map(a => {
    if (a === 'REQUESTER') return 'Requester';
    if (a === 'COMPANY') return myAudience === 'COMPANY' ? 'You' : 'Company';
    if (a === 'INSPECTOR') return myAudience === 'INSPECTOR' ? 'You' : 'Inspector';
    return a;
  });
  return { text: `SHARED · ${labels.join(', ')}`, shared: true };
};

const AttachmentRow = ({ att, myAudience, srHasCompany, onDelete, onShare }) => {
  const pill = computePillLabel(att, myAudience, srHasCompany);
  // Prefer the backend-emitted isMine; fall back to canDelete for pre-Phase-1A
  // responses so the button doesn't vanish during a partial rollout.
  const canShare = att.isMine === true || (att.isMine == null && att.canDelete === true);
  return (
    <div className="jp-report-file-row jp-doc-row">
      <FileTextIcon />
      <div className="jp-doc-info">
        <span className="jp-doc-name" title={att.fileName}>{att.fileName}</span>
        <span className="jp-doc-meta">
          {formatFileSize(att.fileSize)}
          {att.uploadedAt ? `${att.fileSize ? ' · ' : ''}${fmtDate(att.uploadedAt)}` : ''}
        </span>
      </div>
      <span
        className={`jp-doc-pill ${pill.shared ? 'jp-doc-pill-shared' : 'jp-doc-pill-private'}`}
        title={pill.text}
      >
        {pill.text}
      </span>
      {canShare && onShare && (
        <button
          type="button"
          className="jp-report-file-icon-btn jp-doc-share-btn"
          title="Share with additional audiences"
          aria-label="Share with additional audiences"
          onClick={() => onShare(att)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className="jp-report-file-icon-btn"
        title={att.fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
        aria-label={att.fileUrl ? 'View' : 'File unavailable — try refreshing the page'}
        disabled={!att.fileUrl}
        onClick={() => att.fileUrl && window.open(att.fileUrl, '_blank', 'noopener,noreferrer')}
      >
        <EyeIcon />
      </button>
      {onDelete && (
        <button type="button" className="jp-report-file-icon-btn jp-report-file-delete" title="Delete" onClick={onDelete}>
          <TrashIcon />
        </button>
      )}
    </div>
  );
};

// Docs tab — real report attachments split into "Your Uploads" vs "From the
// Agent" (mirrors mobile's DocsTab), falling back to the empty dropzone when
// there's nothing on file yet.
const DocsTab = ({ attachments, onUploadClick, onDeleteClick, onShareClick, myAudience, srHasCompany }) => {
  const isMine = (a) => a.uploadedByRole === 'PROVIDER' || (a.uploadedByRole == null && a.canDelete);
  const myUploads = attachments.filter(isMine);
  const agentUploads = attachments.filter(a => !isMine(a));

  if (attachments.length === 0) return <EmptyDocs onUploadClick={onUploadClick} />;

  return (
    <div className="jp-docs-panel">
      <div className="jp-docs-row-header">
        <p className="jp-docs-section-label">Your Uploads</p>
        <button type="button" className="jp-docs-upload-btn" onClick={onUploadClick}>
          <UploadIcon size={12} />Upload
        </button>
      </div>
      <p className="jp-docs-hint">
        Docs tab · private to you until shared. Tap Share on a file to grant visibility.
      </p>
      {myUploads.length > 0 ? (
        myUploads.map(att => (
          <AttachmentRow
            key={att.id || att.fileUrl}
            att={att}
            myAudience={myAudience}
            srHasCompany={srHasCompany}
            onDelete={att.canDelete ? () => onDeleteClick(att) : null}
            onShare={onShareClick}
          />
        ))
      ) : (
        <p className="jp-doc-empty-text">No uploads yet</p>
      )}

      {agentUploads.length > 0 && (
        <>
          <p className="jp-docs-section-label" style={{ marginTop: 16 }}>From the Agent</p>
          {agentUploads.map(att => (
            <AttachmentRow
              key={att.id || att.fileUrl}
              att={att}
              myAudience={myAudience}
              srHasCompany={srHasCompany}
              onDelete={null}
              onShare={onShareClick}
            />
          ))}
        </>
      )}
    </div>
  );
};

// ── Sidebar nav icons ──────────────────────────────────────────────────────────
const TimelineNavIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
);
const DocsNavIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
);
const ChatNavIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
);
const BillingNavIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

// Billing tab helpers — kept at module scope so the JSX inside the
// component stays declarative. Currency + numeric coercion mirror the
// SP mobile ServiceRequestDetailScreen Billing tab so both platforms
// read the same values identically.
const toBillingNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(n) ? n : null;
};

const formatBillingUsd = (amount) => {
  if (amount === null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
};


// ── Main page component ───────────────────────────────────────────────────────
const JobDetailPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [activeTab, setActiveTab] = useState('timeline');

  const [job, setJob]               = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError]     = useState(null);

  const [timeline, setTimeline]               = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError]     = useState(null);
  const [timelineKey, setTimelineKey]         = useState(0);
  const [actionLoading, setActionLoading]     = useState(null); // null | "<stepNumber>_<ACTION>"
  // Transient error banner for status-update/upload/delete failures — shown
  // alongside the timeline (not replacing it, unlike timelineError) since
  // these previously failed completely silently.
  const [actionError, setActionError]         = useState(null);

  // Upload Report step — { [stepKey]: {attachmentId, fileName, fileUrl} }
  const [jobReports, setJobReports]           = useState({});
  const [reportUploading, setReportUploading] = useState(null); // stepKey currently uploading, 'docs', or null
  const [deleteReportConfirm, setDeleteReportConfirm] = useState(null); // {attachmentId, fileName} | null
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const pendingReportStepRef = useRef(null); // step being uploaded to, or null for a docs-tab upload
  const reportFileInputRef   = useRef(null);

  // Documents tab — the SR's full report-attachments list (both the SP's own
  // uploads and the agent's), independent of the per-step jobReports above.
  const [reportAttachments, setReportAttachments] = useState([]);

  // "Due By Date" / "Due By Time" / "Requested By" shown on the Request
  // Received timeline step — mirrors mobile's Job Details screen (see
  // formatRequestedDue/formatRequestedTime above). Raw ISO strings are kept
  // (not pre-formatted) so they re-render correctly once viewerTimezone
  // resolves from the SP-profile fetch below, whichever effect finishes last.
  const [requestedDateRaw, setRequestedDateRaw] = useState(null);
  const [requestedTimeRaw, setRequestedTimeRaw] = useState(null);
  // Provider-set schedule (step_3 Set-schedule flow). Populated once the SP
  // hits Save; non-null while step_3 is IN_PROGRESS with a proposed value.
  // Drives the "schedule set" view on the timeline row — plain-text
  // date+time display + a Reschedule button in place of the Set-schedule
  // mini-form.
  const [proposedDateRaw, setProposedDateRaw] = useState(null);
  const [proposedTimeRaw, setProposedTimeRaw] = useState(null);
  const [requesterName, setRequesterName]   = useState('');

  // The viewing SP's own timezone, derived from their Service Provider
  // profile's State (GET /api/v1/users/me/sp-profile — the address the SP
  // actually maintains; the base user/localStorage `state` isn't reliably
  // synced for SP accounts since SpProfilePage saves through this endpoint
  // instead). Starts from the generic localStorage-based fallback so nothing
  // renders blank before the fetch below resolves.
  const [viewerTimezone, setViewerTimezone] = useState(() => getViewerTimezone());

  // vendorType decides which two audiences the Share picker offers
  // (COMPANY uploader → Requester + Inspector; INDIVIDUAL → Requester + Company).
  // Fetched once on mount from /users/me/sp-profile; null on failure means the
  // picker falls back to Requester + Company (the more common uploader case).
  const [myVendorType, setMyVendorType] = useState(null);
  // Distinguishes an INDIVIDUAL freelancer (null → picker offers only
  // Requester per spec) from an on-staff inspector (parent set → picker
  // offers Requester + Company). Both are INDIVIDUAL vendorType, so
  // vendorType alone isn't enough to decide.
  const [myParentCompanyUserId, setMyParentCompanyUserId] = useState(null);
  const [sharePickerAtt, setSharePickerAtt] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState(null);

  // Self DB id — same source SpDashboardHeader/JobsPage use. Only meaningful
  // for COMPANY viewers: when job.providerId !== companyUserId the PENDING
  // job has been routed to one of our on-staff inspectors and the Company
  // admin should see Re-Assign in place of Accept/Decline (which the backend
  // 403s them from anyway).
  const [companyUserId, setCompanyUserId] = useState(null);
  // Re-assign modal state (mirrors JobsPage.js — same modal, same endpoint).
  const [assignJob, setAssignJob] = useState(null);
  const [assignMode, setAssignMode] = useState('assign'); // 'assign' | 'reassign'
  const [vendors, setVendors] = useState(null);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorsError, setVendorsError] = useState(null);

  // Role guard
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // One-shot vendorType fetch for the Share picker. Failures are silent —
  // the picker just uses its default (Requester + Company) fallback so
  // the flow keeps working; the server strips uploader-own audiences.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const vt = typeof resp.data?.vendorType === 'string'
          ? resp.data.vendorType.toUpperCase()
          : null;
        if (vt === 'COMPANY' || vt === 'INDIVIDUAL') setMyVendorType(vt);
        const parent = typeof resp.data?.parentCompanyUserId === 'string'
          ? resp.data.parentCompanyUserId
          : null;
        if (parent) setMyParentCompanyUserId(parent);
        if (typeof resp.data?.state === 'string' && resp.data.state.trim()) {
          setViewerTimezone(resolveTimezoneFromState(resp.data.state));
        }
      } catch {
        // silent — picker fallback covers it; viewerTimezone keeps its
        // localStorage-based initial value.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Self DB id, read off the stored profile (same pattern as JobsPage.js).
  // Failure just leaves companyUserId null, which hides the Re-Assign action.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (stored.id) setCompanyUserId(stored.id);
    } catch {
      // Non-critical — Re-Assign just stays hidden without a self id to compare.
    }
  }, []);

  // Re-assign modal handlers — same wire shape as JobsPage.js. Opened from
  // both the timeline "Waiting for {inspector}" block (Company viewer, step 2,
  // PENDING routed to a staff inspector) and the sidebar/action row we may
  // add later.
  const openAssignModal = async (jobArg, mode = 'assign') => {
    setAssignJob(jobArg);
    setAssignMode(mode);
    setVendorsLoading(true);
    setVendorsError(null);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.companyVendors, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let vendorList = extractArray(res.data);
      if (mode === 'reassign' && jobArg?.providerId) {
        // Backend 400s if the picker returns the same inspector currently
        // holding the job — drop them client-side to keep the list clean.
        vendorList = vendorList.filter((v) => v.userId !== jobArg.providerId);
      }
      setVendors(vendorList);
    } catch {
      setVendorsError('Failed to load your service providers. Please try again.');
    } finally {
      setVendorsLoading(false);
    }
  };

  const closeAssignModal = () => {
    setAssignJob(null);
    setAssignMode('assign');
    setVendors(null);
    setVendorsError(null);
  };

  const handleAssignSubmit = async (inspectorUserId) => {
    const token = await getFirebaseToken();
    const endpoint = assignMode === 'reassign'
      ? API_ENDPOINTS.reassignInspector(assignJob.id)
      : API_ENDPOINTS.assignInspector(assignJob.id);
    await apiClient.patch(
      endpoint,
      { inspectorUserId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    closeAssignModal();
    // Refetch timeline — status stays PENDING but providerId + providerName
    // change, which the "Waiting for..." copy and card render both key off.
    setTimelineKey((k) => k + 1);
  };

  // Fetch job + timeline (same endpoint returns the full service-request object
  // with the timeline embedded — see serviceRequestTimeline API doc comment)
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setJobLoading(true);
    setTimelineLoading(true);
    setJobError(null);
    setTimelineError(null);
    setActionError(null);

    getFirebaseToken()
      .then(token =>
        apiClient.get(API_ENDPOINTS.serviceRequestTimeline(jobId), {
          headers: { Authorization: `Bearer ${token}` },
        })
      )
      .then(r => {
        if (cancelled) return;
        const data = r.data && typeof r.data === 'object' ? r.data : {};
        setJob(normalizeJob(data));
        setRequestedDateRaw(data.requestedDate || null);
        setRequestedTimeRaw(data.requestedTime || null);
        setProposedDateRaw(data.proposedDate || null);
        setProposedTimeRaw(data.proposedTime || null);
        setRequesterName(cleanName((data.requesterName || '').trim()) || 'Agent');
        setReportAttachments(Array.isArray(data.reportAttachments) ? data.reportAttachments.map(normalizeAttachment) : []);

        // Normalize: API may return { steps:[...] }, { data:{ steps:[...] } }, [...] directly,
        // or (the job-detail endpoint's actual shape) the full service-request object with
        // the timeline embedded as a `timeline` array field.
        let normalized;
        if (Array.isArray(data)) {
          normalized = { steps: data };
        } else if (Array.isArray(data?.timeline)) {
          normalized = { steps: data.timeline };
        } else if (data?.steps) {
          normalized = data;
        } else if (data?.data?.steps) {
          normalized = data.data;
        } else if (Array.isArray(data?.data)) {
          normalized = { steps: data.data };
        } else {
          normalized = data;
        }
        setTimeline(normalized);
      })
      .catch(() => {
        if (cancelled) return;
        setJobError('Failed to load job. Please try again.');
        setTimelineError('Failed to load timeline.');
      })
      .finally(() => {
        if (cancelled) return;
        setJobLoading(false);
        setTimelineLoading(false);
      });

    return () => { cancelled = true; };
  }, [jobId, timelineKey]);

  // FCM foreground listener — scoped to THIS SR. Refresh job + timeline
  // when the requester (or another company inspector) mutates the SR
  // under us. Push-based only; polling / focus refresh was rejected
  // 2026-08-31 EOD. Silent no-op when web push isn't wired up — the
  // failed-accept/decline handler above still refetches on stale click.
  useEffect(() => {
    if (!jobId) return undefined;
    return subscribeToSrEvents(
      () => setTimelineKey((k) => k + 1),
      { serviceRequestId: jobId },
    );
  }, [jobId]);

  // Pick up a report already on file for this job's steps (in case the
  // timeline API embeds it) without clobbering anything this session already
  // uploaded/knows about locally.
  useEffect(() => {
    if (!timeline?.steps) return;
    setJobReports(prev => {
      let changed = false;
      const merged = { ...prev };
      timeline.steps.forEach(step => {
        const stepKey = getStepKey(step);
        if (merged[stepKey]) return;
        const found = extractStepReport(step);
        if (found) { merged[stepKey] = found; changed = true; }
      });
      return changed ? merged : prev;
    });
  }, [timeline]);

  const handleTimelineAction = async (step, action, extra = {}) => {
    if (!jobId || actionLoading) return;
    // The agent can cancel this job's service request at any point from the
    // transaction's Timeline (cross-icon → confirm → DELETE /calendar/events/{id}),
    // which the backend turns into cancelling the SR rather than deleting it once
    // it's been sent to a provider. That only ever touches the SR's own status —
    // not any already-rendered timeline step — so a step's buttons can still be
    // on screen (or a stale click already queued) after cancellation. Guard here
    // as the actual gate; hiding the buttons below is the normal-path UX, this is
    // the safety net for whatever slips past it.
    if (job?.status === 'CANCELLED') {
      setActionError('This job has been cancelled and no longer requires any action.');
      return;
    }
    const key = `${step.stepNumber}_${action}`;
    setActionLoading(key);
    try {
      const token = await getFirebaseToken();
      let body;
      if (action === 'ACCEPT' || action === 'DECLINE') {
        body = { scope: 'REQUEST', status: action === 'ACCEPT' ? 'ACCEPTED' : 'DECLINED' };
      } else {
        const entryId = step.timelineEntryId || step.entryId || `step_${step.stepNumber}`;
        // Step 3 (CONFIRM_SCHEDULE) also carries proposedDate/proposedTime
        // when the provider sets/edits the schedule. Merged via `extra`.
        body = { scope: 'TIMELINE', timelineEntryId: entryId, status: 'COMPLETED', ...extra };
      }
      await apiClient.patch(
        API_ENDPOINTS.updateServiceStatus(jobId),
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionError(null);
      setTimelineKey(k => k + 1);
    } catch (error) {
      console.error('Error updating job status:', error);
      // Prefer the server's own message so 403s ("Only the assigned provider
      // can change this service request's status") land in front of the user
      // instead of the generic fallback. Only fall back if the server didn't
      // send a body (network error, gateway 5xx, etc.).
      const serverMsg = error?.response?.data?.message;
      const status = error?.response?.status;

      // requireSrState rejection: the SR reached a terminal state between
      // this page's last fetch and the button click — most commonly the
      // requester cancelling from the transaction Timeline while the SP had
      // JobDetail open. The mobile SP has an FCM listener that refreshes
      // automatically; web deliberately does not (product decision
      // 2026-08-31 — no polling / auto-refresh), so surface a friendly
      // reason and force one refetch here so the doomed buttons hide.
      const raw = (serverMsg || '').toLowerCase();
      if (raw.includes('from cancelled')) {
        setActionError('This service request was cancelled by the requester.');
        setTimelineKey(k => k + 1);
        return;
      }
      if (raw.includes('from declined') || raw.includes('from completed') || raw.includes('from expired')) {
        setActionError('This service request is no longer active.');
        setTimelineKey(k => k + 1);
        return;
      }

      const fallback = status === 403
        ? 'Only the assigned inspector can accept or decline this job.'
        : 'Failed to update status. Please try again.';
      setActionError(serverMsg || fallback);
    } finally {
      setActionLoading(null);
    }
  };

  const triggerReportUpload = (step) => {
    pendingReportStepRef.current = step;
    reportFileInputRef.current?.click();
  };

  // Docs-tab upload isn't tied to a specific timeline step — the endpoint
  // accepts a file for the job as a whole (mirrors mobile's "Your Uploads"
  // upload button), so this just clears the pending step ref.
  const triggerDocsUpload = () => {
    pendingReportStepRef.current = null;
    reportFileInputRef.current?.click();
  };

  // Billing tab — inline edit state + save handler. Only the assigned
  // provider may PATCH (backend enforces caller == providerId → 403).
  const [isBillingEditing, setIsBillingEditing] = useState(false);
  const [draftBilledAmount, setDraftBilledAmount] = useState('');
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingError, setBillingError] = useState('');

  const startBillingEdit = () => {
    const current = job?.billedAmount;
    setDraftBilledAmount(
      current === null || current === undefined || current === ''
        ? ''
        : String(current)
    );
    setBillingError('');
    setIsBillingEditing(true);
  };

  const cancelBillingEdit = () => {
    setBillingError('');
    setIsBillingEditing(false);
  };

  const saveBilling = async () => {
    if (!job?.id) return;
    const parsed = draftBilledAmount.trim() === '' ? null : Number(draftBilledAmount);
    if (parsed === null || !Number.isFinite(parsed) || parsed < 0) {
      setBillingError('Enter a non-negative amount.');
      return;
    }
    setSavingBilling(true);
    setBillingError('');
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.patch(
        `${API_BASE_URL}/api/v1/users/me/service-requests/${job.id}/billing`,
        { billedAmount: parsed },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const updated = resp.data?.data || resp.data || {};
      // Optimistic merge — normalizeJob would reset display-derived fields
      // we haven't touched (address, provider*, etc.) so just splice the
      // billing amounts back onto the current normalised job.
      setJob((prev) => (prev ? {
        ...prev,
        estimatedAmount:    updated.estimatedAmount ?? prev.estimatedAmount,
        billedAmount:       updated.billedAmount ?? prev.billedAmount,
        finalPaymentAmount: updated.finalPaymentAmount ?? prev.finalPaymentAmount,
      } : prev));
      setIsBillingEditing(false);
    } catch (err) {
      setBillingError(err?.response?.data?.message || err?.message || 'Save failed.');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleReportFileChange = async (e) => {
    const file = e.target.files?.[0];
    const step = pendingReportStepRef.current;
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file || !jobId) return;

    const stepKey = step ? getStepKey(step) : null;
    setReportUploading(stepKey || 'docs');
    try {
      const token = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file);
      const resp = await apiClient.post(
        API_ENDPOINTS.uploadServiceReport(jobId),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // Unset the client's default JSON content-type so the browser sets
            // multipart/form-data with the correct boundary itself.
            'Content-Type': undefined,
          },
        }
      );
      const d = resp.data?.data ?? resp.data ?? {};
      // The endpoint returns the full, refreshed service-request object (not a
      // bare attachment DTO) — the just-uploaded attachment is the last entry
      // in its reportAttachments/attachments array. Fall back to flat fields
      // in case an older/different response shape is ever returned.
      const attachments = Array.isArray(d.reportAttachments) ? d.reportAttachments
        : Array.isArray(d.attachments) ? d.attachments
        : [];
      const latest = attachments.length > 0 ? attachments[attachments.length - 1] : null;
      const uploaded = {
        attachmentId: latest?.attachmentId || latest?.id || d.attachmentId || d.reportId || null,
        fileName: latest?.fileName || latest?.filename || latest?.name || d.fileName || file.name,
        fileUrl: latest?.fileUrl || latest?.url || latest?.downloadUrl || latest?.viewUrl || d.fileUrl || null,
      };
      if (stepKey) {
        setJobReports(prev => ({ ...prev, [stepKey]: uploaded }));
      }
      if (attachments.length > 0) {
        setReportAttachments(attachments.map(normalizeAttachment));
      }
      setActionError(null);
      setTimelineKey(k => k + 1);
    } catch (error) {
      console.error('Error uploading report:', error);
      setActionError('Failed to upload report. Please try again.');
    } finally {
      setReportUploading(null);
    }
  };

  const handleShareSubmit = async (audiences) => {
    if (!sharePickerAtt || !jobId) return;
    setIsSharing(true);
    setShareError(null);
    try {
      const token = await getFirebaseToken();
      const resp = await apiClient.patch(
        API_ENDPOINTS.shareServiceAttachment(jobId, sharePickerAtt.id),
        { audiences },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = resp.data?.data ?? resp.data ?? {};
      const fresh = Array.isArray(d.reportAttachments) ? d.reportAttachments : [];
      if (fresh.length > 0) {
        setReportAttachments(fresh.map(normalizeAttachment));
      }
      setSharePickerAtt(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to share. Please try again.';
      setShareError(msg);
    } finally {
      setIsSharing(false);
    }
  };

  const openDeleteReportConfirm = (step, report) => {
    setDeleteReportConfirm({ attachmentId: report.attachmentId, fileName: report.fileName });
  };
  const openDeleteAttachmentConfirm = (att) => {
    setDeleteReportConfirm({ attachmentId: att.id, fileName: att.fileName });
  };
  const closeDeleteReportConfirm = () => { if (!isDeletingReport) setDeleteReportConfirm(null); };

  // Shared by both the per-step report row (Timeline tab) and the Documents
  // tab — deletes by attachmentId and scrubs it out of whichever local state
  // happens to reference it, so both tabs stay in sync either way.
  const confirmDeleteReport = async () => {
    if (!deleteReportConfirm || !jobId) return;
    const { attachmentId } = deleteReportConfirm;
    setIsDeletingReport(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(
        API_ENDPOINTS.deleteServiceReport(jobId, attachmentId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobReports(prev => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => { if (v.attachmentId !== attachmentId) next[k] = v; });
        return next;
      });
      setReportAttachments(prev => prev.filter(a => a.id !== attachmentId));
      setActionError(null);
      setTimelineKey(k => k + 1);
      setDeleteReportConfirm(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      setActionError('Failed to delete report. Please try again.');
      // keep the modal open so the user can see it failed and retry/cancel
    } finally {
      setIsDeletingReport(false);
    }
  };

  const handleBackToJobs = () => navigate('/jobs');

  // Show loading state
  if (jobLoading && !job) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper">
          <div className="txn-layout-container">
            <div className="txn-main-area">
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div className="txn-page-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                <h2>Loading Job...</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if no job found
  if (!job) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper">
          <div className="txn-layout-container">
            <div className="txn-main-area">
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                <h2>Job Not Found</h2>
                <p>{jobError || "The job you're looking for doesn't exist or you don't have access to it."}</p>
                <button className="txn-back-button" onClick={handleBackToJobs} style={{ marginTop: '20px' }}>
                  <span>←</span>
                  <span>Back to Jobs</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Bounced-inspector view: THIS user was the SR's previous provider and the
  // SR has since been bounced back to the company's queue (status flipped to
  // AWAITING_ASSIGNMENT, routedByCompanyId still set). From this inspector's
  // POV the SR is effectively DECLINED — surface the Declined pill and
  // suppress the step 2 Accept/Decline pair (which the backend also 403s them
  // from anyway). The Company's own view is unaffected.
  const isBouncedInspectorView = !!companyUserId
    && !!job.previousProviderId
    && companyUserId === job.previousProviderId
    && job.status === 'AWAITING_ASSIGNMENT'
    && !!job.routedByCompanyId;
  const pill = isBouncedInspectorView
    ? STATUS_PILL.DECLINED
    : (STATUS_PILL[job.status] || { label: job.status, cls: 'cancelled' });

  const NAV_ITEMS = [
    { key: 'timeline', label: 'Timeline',  icon: <TimelineNavIcon /> },
    { key: 'docs',     label: 'Documents', icon: <DocsNavIcon /> },
    { key: 'chat',     label: 'Chat',      icon: <ChatNavIcon /> },
    { key: 'billing',  label: 'Billing',   icon: <BillingNavIcon /> },
  ];

  return (
    <div className="txn-detail-container">
      <div className="txn-layout-wrapper">
        <div className="txn-layout-container">
          {/* Sidebar */}
          <div className="txn-sidebar">
            <div className="txn-sidebar-header">
              <button className="txn-back-button-small" onClick={handleBackToJobs}>
                <span>←</span>
                <span>Back</span>
              </button>
              <div className="txn-sidebar-title">{job.address}</div>
              <div className="txn-sidebar-address">{job.serviceTypeLabel} · {job.clientName}</div>
              <div className="txn-sidebar-status-row">
                <span className={`txn-sidebar-status-badge status-job-${pill.cls}`}>{pill.label}</span>
              </div>
              <DescriptionBox text={job.description} />
            </div>

            <div className="txn-sidebar-nav">
              {NAV_ITEMS.map(({ key, label, icon }) => (
                <div
                  key={key}
                  className={`txn-sidebar-nav-item ${activeTab === key ? 'active' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  <div className="txn-sidebar-nav-label">
                    {icon}
                    <span>{label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="txn-main-area">
            <div className="txn-content-section">
              <div className="txn-content-header">
                <h2 className="txn-content-title">
                  {activeTab === 'timeline' ? 'Timeline'
                    : activeTab === 'docs' ? 'Documents'
                    : activeTab === 'billing' ? 'Billing'
                    : 'Chat'}
                </h2>
              </div>

              {actionError && (
                <div className="jp-action-error-banner">
                  <span>{actionError}</span>
                  <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              {activeTab === 'timeline' && (
                <DynamicTimeline
                  timeline={timeline}
                  loading={timelineLoading}
                  error={timelineError}
                  onAction={handleTimelineAction}
                  actionLoading={actionLoading}
                  jobReports={jobReports}
                  reportUploading={reportUploading}
                  onUploadReportClick={triggerReportUpload}
                  onDeleteReportClick={openDeleteReportConfirm}
                  reportAttachments={reportAttachments}
                  onDeleteAttachmentClick={openDeleteAttachmentConfirm}
                  requestedDueBy={formatRequestedDue(requestedDateRaw, viewerTimezone)}
                  requestedDueTime={formatRequestedTime(requestedTimeRaw, viewerTimezone)}
                  scheduledDate={formatRequestedDue(proposedDateRaw, viewerTimezone)}
                  scheduledTime={formatRequestedTime(proposedTimeRaw, viewerTimezone)}
                  requesterName={requesterName}
                  description={job.description}
                  viewerVendorType={myVendorType}
                  companyUserId={companyUserId}
                  jobStatus={job.status}
                  jobProviderId={job.providerId}
                  jobProviderName={job.providerName}
                  isBouncedInspectorView={isBouncedInspectorView}
                  onReassignClick={() => openAssignModal(job, 'reassign')}
                  onAssignClick={() => openAssignModal(job, 'assign')}
                />
              )}
              {activeTab === 'docs' && (
                <DocsTab
                  attachments={reportAttachments}
                  onUploadClick={triggerDocsUpload}
                  onDeleteClick={openDeleteAttachmentConfirm}
                  onShareClick={(att) => { setShareError(null); setSharePickerAtt(att); }}
                  myAudience={myVendorType === 'COMPANY' ? 'COMPANY' : myVendorType === 'INDIVIDUAL' ? 'INSPECTOR' : null}
                  srHasCompany={myVendorType === 'COMPANY' || !!myParentCompanyUserId}
                />
              )}
              {activeTab === 'chat' && (
                <div className="jp-chat-tab">
                  <div className="jp-chat-section-heading">Requester</div>
                  {job.requesterUid ? (
                    <button
                      type="button"
                      className="jp-requester-card"
                      onClick={() => {
                        const name = job.requesterName || job.clientName || '';
                        navigate(`/conversations/sp?userId=${encodeURIComponent(job.requesterUid)}&userName=${encodeURIComponent(name)}`);
                      }}
                      title={`Chat with ${job.requesterName || job.clientName}`}
                    >
                      <div className="jp-requester-avatar">
                        {((job.requesterName || job.clientName || 'R').trim().charAt(0) || 'R').toUpperCase()}
                      </div>
                      <div className="jp-requester-info">
                        <div className="jp-requester-name">{job.requesterName || job.clientName}</div>
                        <div className="jp-requester-role">Agent</div>
                      </div>
                      <div className="jp-requester-chat-btn" aria-label="Chat">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                    </button>
                  ) : (
                    <div className="jp-chat-placeholder">
                      <MessageIcon />
                      <p>Requester not available for this job yet.</p>
                    </div>
                  )}

                  {/* Inspector chat — only rendered for a Company viewer
                      whose SR has been routed to one of their on-staff
                      inspectors. Individual freelancers and Company-view
                      SRs still sitting on the Company itself (providerId ==
                      self) don't get a second card here. Same Firestore
                      chat wiring as the Requester card. */}
                  {myVendorType === 'COMPANY'
                    && job.providerId
                    && companyUserId
                    && job.providerId !== companyUserId && (
                    <>
                      <div className="jp-chat-section-heading" style={{ marginTop: 20 }}>Inspector</div>
                      {job.providerFirebaseUid ? (
                        <button
                          type="button"
                          className="jp-requester-card"
                          onClick={() => {
                            const name = job.providerName || 'Inspector';
                            navigate(`/conversations/sp?userId=${encodeURIComponent(job.providerFirebaseUid)}&userName=${encodeURIComponent(name)}`);
                          }}
                          title={`Chat with ${job.providerName || 'the assigned inspector'}`}
                        >
                          <div className="jp-requester-avatar">
                            {((job.providerName || 'I').trim().charAt(0) || 'I').toUpperCase()}
                          </div>
                          <div className="jp-requester-info">
                            <div className="jp-requester-name">{job.providerName || 'Assigned inspector'}</div>
                            <div className="jp-requester-role">Inspector</div>
                          </div>
                          <div className="jp-requester-chat-btn" aria-label="Chat">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                          </div>
                        </button>
                      ) : (
                        <div className="jp-chat-placeholder">
                          <MessageIcon />
                          <p>Inspector chat not available yet.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'billing' && job && (() => {
                const est = toBillingNumber(job.estimatedAmount);
                const billed = toBillingNumber(job.billedAmount);
                const paid = toBillingNumber(job.finalPaymentAmount);
                const showMismatch = est !== null && billed !== null && est !== billed;
                const providerInvoices = (reportAttachments || []).filter(
                  (a) => a.uploadedByRole === 'PROVIDER'
                );
                const canMutateBilling = myVendorType === 'INDIVIDUAL' || myVendorType === 'COMPANY';
                return (
                  <div className="jp-billing-tab">
                    {showMismatch && (
                      <div className="jp-billing-warn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <span>Billed Amount differs from Estimated Amount</span>
                      </div>
                    )}

                    <div className="jp-billing-grid">
                      <div className="jp-billing-card">
                        <div className="jp-billing-card-label">ESTIMATED AMOUNT</div>
                        {est !== null ? (
                          <div className="jp-billing-card-value">{formatBillingUsd(est)}</div>
                        ) : (
                          <div className="jp-billing-card-empty">Not provided</div>
                        )}
                        <div className="jp-billing-card-hint">Set when job was requested</div>
                      </div>

                      <div className={`jp-billing-card jp-billing-card--interactive${isBillingEditing ? ' jp-billing-card--editing' : ''}`}>
                        <div className="jp-billing-card-label">BILLED AMOUNT</div>
                        {isBillingEditing ? (
                          <div className="jp-billing-edit-inline">
                            <input
                              type="number"
                              className="jp-billing-input"
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={draftBilledAmount}
                              onChange={(e) => setDraftBilledAmount(e.target.value)}
                              disabled={savingBilling}
                              autoFocus
                            />
                            <div className="jp-billing-edit-buttons">
                              <button
                                type="button"
                                className="jp-billing-btn jp-billing-btn-secondary"
                                onClick={cancelBillingEdit}
                                disabled={savingBilling}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="jp-billing-btn jp-billing-btn-primary"
                                onClick={saveBilling}
                                disabled={savingBilling}
                              >
                                {savingBilling ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                            {billingError && (
                              <div className="jp-billing-error">{billingError}</div>
                            )}
                          </div>
                        ) : billed !== null ? (
                          <div className="jp-billing-card-value">{formatBillingUsd(billed)}</div>
                        ) : (
                          <div className="jp-billing-card-empty">Not yet recorded</div>
                        )}
                        {!isBillingEditing && (
                          canMutateBilling ? (
                            <button
                              type="button"
                              className="jp-billing-card-action"
                              onClick={startBillingEdit}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                              </svg>
                              {billed === null ? 'Add amount' : 'Edit amount'}
                            </button>
                          ) : (
                            <div className="jp-billing-card-hint">From submitted invoice</div>
                          )
                        )}
                      </div>

                      <div className="jp-billing-card jp-billing-card--readonly">
                        <div className="jp-billing-card-label">PAYMENT RECEIVED</div>
                        {paid !== null ? (
                          <div className="jp-billing-card-value">{formatBillingUsd(paid)}</div>
                        ) : (
                          <div className="jp-billing-card-empty">Not yet recorded</div>
                        )}
                        <div className="jp-billing-card-hint jp-billing-card-hint--lock">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                          Read-only — entered by requester
                        </div>
                      </div>
                    </div>

                    <div className="jp-billing-section-row">
                      <h3 className="jp-billing-section-header">Submitted Invoice</h3>
                      {canMutateBilling && (
                        <button
                          type="button"
                          className="jp-billing-upload-btn"
                          onClick={triggerDocsUpload}
                          disabled={reportUploading}
                          title="Upload an invoice PDF or image"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          {reportUploading ? 'Uploading…'
                            : providerInvoices.length === 0 ? 'Upload Invoice'
                            : 'Upload another'}
                        </button>
                      )}
                    </div>
                    {providerInvoices.length === 0 ? (
                      <div className="jp-billing-invoice-empty">
                        <div className="jp-billing-invoice-empty-icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                        </div>
                        <div className="jp-billing-invoice-empty-body">
                          <div className="jp-billing-invoice-empty-title">No invoice uploaded yet</div>
                          <div className="jp-billing-invoice-empty-sub">
                            {canMutateBilling
                              ? 'Tap Upload Invoice above to attach a PDF or image. Files uploaded here also appear on the Documents tab.'
                              : 'The assigned inspector will submit an invoice when the job is done.'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="jp-billing-invoice-list">
                        {providerInvoices.slice(0, 5).map((att) => {
                          // Same URL-field fallback chain the Docs tab uses —
                          // reportAttachments come back with different key
                          // names depending on whether they were minted by
                          // the SR list endpoint or the SR detail endpoint.
                          const url = att.fileUrl || att.url || att.downloadUrl || att.viewUrl || null;
                          const mime = (att.mimeType || att.contentType || '').toLowerCase();
                          const ext = (att.fileName || '').split('.').pop().toLowerCase();
                          const isImage = mime.startsWith('image/')
                            || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif'].includes(ext);
                          const isPdf = mime === 'application/pdf' || ext === 'pdf';
                          return (
                            <div key={att.id || url} className="jp-billing-invoice-row">
                              <div className="jp-billing-invoice-icon">
                                {isImage ? (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                    <polyline points="21 15 16 10 5 21"/>
                                  </svg>
                                ) : isPdf ? (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <text x="7.5" y="17.5" fontSize="6" fontWeight="700" fill="currentColor" stroke="none">PDF</text>
                                  </svg>
                                ) : (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                )}
                              </div>
                              <div className="jp-billing-invoice-info">
                                <div className="jp-billing-invoice-name">{att.fileName}</div>
                                {att.uploadedAt && (
                                  <div className="jp-billing-invoice-meta">
                                    Submitted {fmtDate(att.uploadedAt)}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                className="jp-billing-invoice-view"
                                disabled={!url}
                                onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
                                title={url ? 'Open in new tab' : 'File link not available'}
                              >
                                View
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input for the "Upload report" action */}
      <input
        type="file"
        ref={reportFileInputRef}
        style={{ display: 'none' }}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        onChange={handleReportFileChange}
      />

      {/* Delete report confirmation — same Yes/No popup design language as
          document deletion elsewhere on the site (TransactionDocuments.js'
          .txn-delete-modal), reproduced under jp- names since that stylesheet
          isn't loaded on this page. */}
      <SharePickerModal
        visible={!!sharePickerAtt}
        attachment={sharePickerAtt}
        uploaderVendorType={myVendorType}
        uploaderParentCompanyUserId={myParentCompanyUserId}
        requesterName={requesterName}
        counterpartyName={job?.providerName || null}
        submitting={isSharing}
        error={shareError}
        onClose={() => { if (!isSharing) { setSharePickerAtt(null); setShareError(null); } }}
        onSubmit={handleShareSubmit}
      />

      <AssignJobModal
        visible={!!assignJob}
        job={assignJob}
        mode={assignMode}
        vendors={vendors}
        vendorsLoading={vendorsLoading}
        vendorsError={vendorsError}
        onClose={closeAssignModal}
        onAssign={handleAssignSubmit}
      />

      {deleteReportConfirm && (
        <div className="jp-delete-modal-overlay" onClick={closeDeleteReportConfirm}>
          <div className="jp-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="jp-delete-modal-icon">
              <TrashIcon size={28} />
            </div>
            <div className="jp-delete-modal-title">Delete Report</div>
            <div className="jp-delete-modal-desc">
              Are you sure you want to delete <strong>"{deleteReportConfirm.fileName || 'this report'}"</strong>? This action cannot be undone.
            </div>
            <div className="jp-delete-modal-actions">
              <button
                className="jp-delete-modal-btn jp-delete-modal-yes"
                onClick={confirmDeleteReport}
                disabled={isDeletingReport}
              >
                {isDeletingReport ? 'Deleting...' : 'Yes'}
              </button>
              <button
                className="jp-delete-modal-btn jp-delete-modal-no"
                onClick={closeDeleteReportConfirm}
                disabled={isDeletingReport}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
